import React, { FC, useEffect } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import FileCopyIcon from "@mui/icons-material/FileCopy";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CategoryIcon from "@mui/icons-material/Category";
import MapIcon from "@mui/icons-material/Map";
import { useNavigate, useLocation } from "react-router-dom";

import { AuthService, RESTRICTED_ROLES } from "../services/auth.service";
import { DRAWER_WIDTH } from "../constants/components";
import { PrimaryAppBar } from "./AppBar";
import { Logo } from "./Logo";

const menus = [
  { name: "View Files", iconName: "viewFiles", pathname: "/" },
  {
    name: "Upload Files",
    iconName: "uploadFiles",
    pathname: "/upload-document",
  },
  { name: "My Uploads", iconName: "myUploads", pathname: "/document" },
  { name: "Push Notification", iconName: "notification", pathname: "/notification" },
  {
    name: "Category",
    iconName: "category",
    pathname: "/category",
  },
  { name: "Manage User", iconName: "manage", pathname: "/manage-user" },

    { name: "Job Safety", iconName: "map", pathname: "/job-safety" },
];

const getIcon = (name: string) => {
  switch (name) {
    case "viewFiles":
      return <FileCopyIcon />;
    case "uploadFiles":
      return <CloudUploadIcon />;
    case "myUploads":
      return <UploadFileIcon />;
    case "notification":
      return <NotificationsActiveIcon />;
    case "category":
      return <CategoryIcon />;
    case "manage":
          return <ManageAccountsIcon />;
      case "map":
          return <MapIcon />;
  }
};
const authService = new AuthService();
const PermanentDrawerLeft: FC = () => {
  const user = authService.getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Filter menus based on user role
  const filteredMenus = user?.userRole && RESTRICTED_ROLES.includes(user.userRole)
    ? menus.filter((menu) => menu.name === "Job Safety")
    : menus;

  const paths = filteredMenus.map((menu) => menu.pathname);
  const initIndex = paths.indexOf(location.pathname);
  const [selectedIndex, setSelectedIndex] = React.useState(
    initIndex !== -1 ? initIndex : 1
  );

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => {
    // Index is into filteredMenus (what's actually rendered), not the full
    // menus list -- for a restricted-role user those two arrays differ in
    // length, so indexing into `menus` here would navigate to the wrong route.
    const menu = filteredMenus[index] || filteredMenus[0];
    setSelectedIndex(index);
    if (menu?.pathname) {
      navigate(menu.pathname);
    }
    setMobileOpen(false);
  };


  useEffect(() => {
    const index = paths.indexOf(location.pathname);
    setSelectedIndex(index !== -1 ? index : 1);
  }, [location.pathname]);

  const drawerContent = (
    <>
      <Logo />
      <Box sx={{ width: "auto", overflow: "hidden" }} role="presentation">
        <List
          component="nav"
          id="nav-list"
          aria-label={filteredMenus.map((menu) => menu.name).join(" ")}
        >
          {filteredMenus.map((menu, index) => (
            <ListItemButton
              key={`nav-${menu.name}`}
              sx={{ width: DRAWER_WIDTH, height: "54px", paddingLeft: "8px" }}
              selected={selectedIndex === index}
              onClick={(
                event: React.MouseEvent<HTMLDivElement, MouseEvent>
              ) => handleListItemClick(event, index)}
            >
              <ListItemIcon sx={{ marginLeft: "10px", color: "inherit" }}>
                {getIcon(menu.iconName)}
              </ListItemIcon>
              <ListItemText primary={menu.name} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <PrimaryAppBar onMenuClick={handleDrawerToggle} />

      {/* Mobile: collapsible drawer toggled via the AppBar's menu button */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
        anchor="left"
      >
        {drawerContent}
      </Drawer>

      {/* Desktop: always-visible drawer */}
      <Drawer
        sx={{
          display: { xs: "none", md: "block" },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
        variant="permanent"
        anchor="left"
      >
        {drawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: "#f8f6f6",
          width: "100%",
        }}
      >
        <Toolbar />
      </Box>

    </Box>
  );
};

export default PermanentDrawerLeft;
